import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { AlertTriangle, Workflow } from 'lucide-react'

import { WorkflowTriggerEntityType } from '@/gen/cortado/v1/cortado_pb'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { formatWorkflowTrigger } from '@/components/settings/workflows/format-conditions'
import { WorkflowExecutionDetailSheet } from '@/components/settings/workflows/workflow-execution-detail-sheet'
import { getErrorDescription } from '@/lib/error-utils'
import { useStartWorkflowExecution, useWorkflowDefinitions } from '@/stores/workflows.store'
import { entitySchemaFor, useWorkflowAuthoringSchema } from '@/stores/workflow-authoring.store'

/** UPPERCASE trigger entity type accepted by the manual-run entry points. */
type RunWorkflowTriggerEntityType = Exclude<keyof typeof WorkflowTriggerEntityType, 'UNSPECIFIED'>

interface RunWorkflowDialogProps {
  /** Inquiry id (lead) or booking id (event) — the execution's source_resource_id. */
  sourceResourceId: string
  /** UPPERCASE trigger entity type this dialog scopes eligible workflows to. */
  triggerEntityType: RunWorkflowTriggerEntityType
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Dialog for manually starting a workflow run against a single lead/event.
 * Lists the org's enabled definitions whose trigger entity matches
 * `triggerEntityType` (filtered client-side from the existing definitions
 * list — no dedicated eligibility RPC), lets the user confirm one, and calls
 * StartWorkflowExecution. A manual run always executes, skipping the
 * definition's trigger conditions.
 */
export function RunWorkflowDialog({
  sourceResourceId,
  triggerEntityType,
  open,
  onOpenChange,
}: RunWorkflowDialogProps) {
  const [selectedId, setSelectedId] = useState<string>()
  const [viewExecutionId, setViewExecutionId] = useState<string>()
  const [detailOpen, setDetailOpen] = useState(false)
  const { workflowDefinitions, isLoading, hasLoaded } = useWorkflowDefinitions({ enabled: open })
  const { startExecution, isPending } = useStartWorkflowExecution()
  const { schema, hasLoaded: schemaHasLoaded } = useWorkflowAuthoringSchema({ enabled: open })

  const entityType = WorkflowTriggerEntityType[triggerEntityType]
  const entitySchema = entitySchemaFor(schema?.entities, entityType)
  const manualRunSupported = entitySchema?.manualRunSupported ?? false

  const stillLoading = (isLoading && !hasLoaded) || !schemaHasLoaded
  const showWorkflowList = !stillLoading && manualRunSupported

  const eligibleWorkflows = useMemo(
    () =>
      workflowDefinitions.filter(
        (definition) => definition.enabled && definition.trigger?.entityType === entityType
      ),
    [workflowDefinitions, entityType]
  )

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSelectedId(undefined)
    }
    onOpenChange(nextOpen)
  }

  const handleConfirm = async () => {
    if (!selectedId) return

    try {
      const response = await startExecution({
        workflowDefinitionId: selectedId,
        entityType,
        sourceResourceId,
      })
      toast.success('Workflow run started', {
        action: {
          label: 'View run',
          onClick: () => {
            setViewExecutionId(response.execution?.id)
            setDetailOpen(true)
          },
        },
      })
      handleOpenChange(false)
    } catch (error) {
      toast.error('Failed to start workflow run', {
        description: getErrorDescription(error),
      })
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Run a workflow</DialogTitle>
          <DialogDescription>
            Manually starts the selected workflow for this record. Its trigger
            conditions are skipped — a manual run always executes.
          </DialogDescription>
        </DialogHeader>

        {stillLoading ? (
          <div className="py-6 text-sm text-muted-foreground">Loading workflows…</div>
        ) : !manualRunSupported ? (
          <Empty className="border border-dashed py-10">
            <EmptyMedia variant="icon">
              <Workflow className="h-5 w-5" />
            </EmptyMedia>
            <EmptyDescription>
              Manual runs aren&apos;t supported for this record type yet.
            </EmptyDescription>
          </Empty>
        ) : eligibleWorkflows.length === 0 ? (
          <Empty className="border border-dashed py-10">
            <EmptyMedia variant="icon">
              <Workflow className="h-5 w-5" />
            </EmptyMedia>
            <EmptyDescription>
              No enabled workflows are set up for this trigger yet.{' '}
              <Link to="/workflows" search={{ view: 'definitions' }}>
                Build one →
              </Link>
            </EmptyDescription>
          </Empty>
        ) : (
          <>
            <RadioGroup value={selectedId} onValueChange={setSelectedId}>
              {eligibleWorkflows.map((definition) => (
                <label
                  key={definition.id}
                  htmlFor={`run-workflow-${definition.id}`}
                  className="flex cursor-pointer items-start gap-3 rounded-md border p-3 hover:bg-accent/40 has-[[data-state=checked]]:border-primary"
                >
                  <RadioGroupItem
                    id={`run-workflow-${definition.id}`}
                    value={definition.id}
                    className="mt-0.5"
                  />
                  <div className="min-w-0 space-y-0.5">
                    <p className="truncate text-sm font-medium">{definition.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      Trigger: {formatWorkflowTrigger(definition.trigger)}
                    </p>
                  </div>
                </label>
              ))}
            </RadioGroup>

            <Alert>
              <AlertTriangle />
              <AlertDescription>
                Conditions are skipped for manual runs, so this executes even
                if the trigger&apos;s conditions wouldn&apos;t normally match.
              </AlertDescription>
            </Alert>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          {showWorkflowList && eligibleWorkflows.length > 0 && (
            <Button onClick={handleConfirm} disabled={!selectedId || isPending}>
              {isPending ? 'Starting…' : 'Run workflow'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
      <WorkflowExecutionDetailSheet
        executionId={viewExecutionId}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />
    </>
  )
}
